/**
 * Pool Violations Panel — read-only overlay control for the relationship graph.
 *
 * Picker for an Item Pool / track + a list of detected anti-patterns
 * (see demo/PRIORITIZATION_ANTIPATTERNS.md). Clicking a violation highlights the
 * involved nodes on the canvas. Purely presentational; detection lives in
 * src/lib/poolViolations.ts.
 */

import { AlertOctagon, AlertTriangle, ShieldCheck, ChevronDown, ChevronUp, Crosshair, Info, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import type { PoolScopeOption, PoolViolationReport, PoolViolation } from '../../../lib/poolViolations';
import { PRIORITIZATION_ANTIPATTERNS } from '../../../lib/prioritizationAntipatterns';

const BLOCK = '#ef4444';
const WARN  = '#f59e0b';
const OK    = '#10b981';

const POOL_VIOLATIONS_DEFINITION =
  'Structural mistakes in a pool or Track that can invalidate or distort a ranking before the session runs.';
const POOL_SCOPE_DEFINITION =
  'An Item Pool is a defined set of items. A Track adds the category and altitude boundary that determines which items are meaningfully comparable.';

interface Props {
  options: PoolScopeOption[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  report: PoolViolationReport | null;
  loading: boolean;
  activeViolationKey: string | null;
  onHighlight: (itemIds: string[] | null, key: string | null) => void;
}

export function PoolViolationsPanel({ options, selectedId, onSelect, report, loading, activeViolationKey, onHighlight }: Props) {
  const { isDark } = useTheme();
  const [open, setOpen] = useState(true);

  const bg = isDark ? '#1e293b' : '#ffffff';
  const border = isDark ? '#334155' : '#e5e7eb';
  const textPri = isDark ? '#e2e8f0' : '#111827';
  const textSec = isDark ? '#94a3b8' : '#6b7280';

  const violations = report?.violations ?? [];
  const clean = !!report && violations.length === 0;

  return (
    <div className="shadow-sm text-xs overflow-visible" style={{ minWidth: 280, maxWidth: 320, backgroundColor: bg, border: `1px solid ${border}` }}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-2 w-full text-left font-semibold transition-colors"
        style={{ color: textPri }}
        title={POOL_VIOLATIONS_DEFINITION}
      >
        <AlertOctagon className="h-3.5 w-3.5" style={{ color: BLOCK }} />
        Pool violations
        {report && !loading && (
          <span className="ml-1 inline-flex items-center gap-1">
            {report.blockCount > 0 && <Badge color={BLOCK} label={`${report.blockCount} block`} />}
            {report.warnCount > 0 && <Badge color={WARN} label={`${report.warnCount} warn`} />}
            {clean && <Badge color={OK} label="clean" />}
          </span>
        )}
        {open ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2 border-t" style={{ borderColor: border }}>
          {/* Picker */}
          <div className="pt-2">
            <label className="flex items-center gap-1 text-[9px] uppercase tracking-wide mb-1 font-semibold" style={{ color: textSec }}>
              Inspect a pool / Track
              <InlineTermTooltip label="Pool and Track" definition={POOL_SCOPE_DEFINITION} />
            </label>
            <select
              value={selectedId ?? ''}
              onChange={(e) => onSelect(e.target.value || null)}
              className="w-full px-2 py-1.5 text-xs border"
              style={{ backgroundColor: isDark ? '#0f172a' : '#fff', color: textPri, borderColor: border }}
            >
              <option value="">— select —</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.kind === 'frame' ? '◆ ' : '▢ '}{o.name} ({o.itemIds.length})
                </option>
              ))}
            </select>
          </div>

          {loading && (
            <div className="flex items-center gap-2 py-3" style={{ color: textSec }}>
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Detecting…
            </div>
          )}

          {!loading && clean && (
            <div className="flex items-center gap-2 py-2" style={{ color: OK }}>
              <ShieldCheck className="h-4 w-4" /> No violations — this pool is comparable.
            </div>
          )}

          {!loading && violations.length > 0 && (
            <div className="space-y-1.5 pt-1">
              {violations.map((v, i) => (
                <ViolationRow
                  key={`${v.patternId}-${i}`}
                  tooltipKey={`${v.patternId}-${i}`}
                  v={v}
                  active={activeViolationKey === `${v.patternId}-${i}`}
                  onClick={() => {
                    const key = `${v.patternId}-${i}`;
                    if (activeViolationKey === key) onHighlight(null, null);
                    else onHighlight(v.itemIds, key);
                  }}
                  textPri={textPri}
                  textSec={textSec}
                  border={border}
                />
              ))}
              <p className="text-[10px] pt-1" style={{ color: textSec }}>
                Detected at pool formation — before a ranking session runs.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Badge({ color, label }: { color: string; label: string }) {
  const definition = label.includes('block')
    ? 'Must be resolved before a valid ranking session can run.'
    : label.includes('warn')
      ? 'Review before ranking; this pattern may distort the result.'
      : 'No detected structural violations in this pool.';
  return (
    <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-bold leading-none border"
      style={{ color, backgroundColor: `${color}1a`, borderColor: `${color}55` }}
      title={definition}>
      {label}
    </span>
  );
}

function InlineTermTooltip({ label, definition }: { label: string; definition: string }) {
  return (
    <span className="relative inline-flex group/term normal-case tracking-normal">
      <span
        tabIndex={0}
        className="inline-flex cursor-help focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
        aria-label={`Explain ${label}`}
      >
        <Info className="h-3 w-3" />
      </span>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 hidden w-72 -translate-y-1/2 border border-[var(--rule)] bg-[var(--bg-raised)] p-3 text-left font-body text-xs font-normal leading-relaxed text-[var(--text-sec)] shadow-lg group-hover/term:block group-focus-within/term:block dark:border-[var(--rule)] dark:bg-[var(--bg-raised)] dark:text-[var(--text-sec)]"
      >
        <span className="mb-1 block font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{label}</span>
        {definition}
      </span>
    </span>
  );
}

function ViolationRow({ v, tooltipKey, active, onClick, textPri, textSec, border }: {
  v: PoolViolation; tooltipKey: string; active: boolean; onClick: () => void; textPri: string; textSec: string; border: string;
}) {
  const color = v.disposition === 'block' ? BLOCK : WARN;
  const Icon = v.disposition === 'block' ? AlertOctagon : AlertTriangle;
  const catalogEntry = PRIORITIZATION_ANTIPATTERNS.find((entry) => entry.patternId === v.patternId);
  const definition = catalogEntry?.structure ?? v.detail;
  const tooltipId = `violation-definition-${tooltipKey}`;
  return (
    <button
      onClick={onClick}
      className="relative group/violation w-full text-left px-2 py-1.5 border transition-colors"
      style={{ borderColor: active ? color : border, backgroundColor: active ? `${color}14` : 'transparent' }}
      aria-describedby={tooltipId}
    >
      <div className="flex items-center gap-1.5">
        <Icon className="h-3 w-3 flex-shrink-0" style={{ color }} />
        <span className="font-semibold leading-none" style={{ color: textPri }}>{v.name}</span>
        <Info className="h-3 w-3 flex-shrink-0 cursor-help" style={{ color: textSec }} aria-hidden="true" />
        <Crosshair className="h-2.5 w-2.5 ml-auto flex-shrink-0" style={{ color: active ? color : textSec }} />
      </div>
      <p className="mt-1 leading-snug" style={{ color: textSec, fontSize: 10 }}>{v.detail}</p>
      <span
        id={tooltipId}
        role="tooltip"
        className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 hidden w-72 -translate-y-1/2 border border-[var(--rule)] bg-[var(--bg-raised)] p-3 text-left font-body text-xs font-normal leading-relaxed text-[var(--text-sec)] shadow-lg group-hover/violation:block group-focus/violation:block dark:border-[var(--rule)] dark:bg-[var(--bg-raised)] dark:text-[var(--text-sec)]"
      >
        <span className="mb-1 block font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          {v.name}{catalogEntry?.aka ? ` · ${catalogEntry.aka}` : ''}
        </span>
        {definition}
      </span>
    </button>
  );
}
